import { useRef, useState } from 'react';
import { Markdown } from '../../../components/Markdown.js';
import { Tabs } from '../../../components/ui.js';

interface ToolbarAction {
  label: string;
  title: string;
  apply: (value: string, start: number, end: number) => { next: string; selectStart: number; selectEnd: number };
}

function wrapSelection(prefix: string, suffix = prefix): ToolbarAction['apply'] {
  return (value, start, end) => {
    const selected = value.slice(start, end) || 'text';
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    return { next, selectStart: start + prefix.length, selectEnd: start + prefix.length + selected.length };
  };
}

function prefixLines(linePrefix: (i: number) => string): ToolbarAction['apply'] {
  return (value, start, end) => {
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const block = value.slice(lineStart, end);
    const prefixed = block
      .split('\n')
      .map((line, i) => linePrefix(i) + line)
      .join('\n');
    const next = value.slice(0, lineStart) + prefixed + value.slice(end);
    return { next, selectStart: lineStart, selectEnd: lineStart + prefixed.length };
  };
}

const TABLE_SKELETON = '\n| Column | Column |\n| --- | --- |\n| … | … |\n';

const ACTIONS: ToolbarAction[] = [
  { label: 'B', title: 'Bold', apply: wrapSelection('**') },
  { label: 'I', title: 'Italic', apply: wrapSelection('_') },
  { label: 'H2', title: 'Heading', apply: prefixLines(() => '## ') },
  { label: '•', title: 'Bulleted list', apply: prefixLines(() => '- ') },
  { label: '1.', title: 'Numbered list', apply: prefixLines((i) => `${i + 1}. `) },
  { label: 'Link', title: 'Insert link', apply: wrapSelection('[', '](https://)') },
  {
    label: 'Table',
    title: 'Insert table',
    apply: (value, _start, end) => ({
      next: value.slice(0, end) + TABLE_SKELETON + value.slice(end),
      selectStart: end + 3,
      selectEnd: end + 9,
    }),
  },
];

export function MarkdownEditor({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Response content',
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const ref = useRef<HTMLTextAreaElement>(null);

  const run = (action: ToolbarAction) => {
    const el = ref.current;
    if (!el) return;
    const { next, selectStart, selectEnd } = action.apply(
      value,
      el.selectionStart,
      el.selectionEnd,
    );
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(selectStart, selectEnd);
    });
  };

  return (
    <div>
      <Tabs
        tabs={[
          { key: 'write' as const, label: 'Write' },
          { key: 'preview' as const, label: 'Preview' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'write' ? (
        <>
          <div className="md-toolbar" role="toolbar" aria-label="Formatting">
            {ACTIONS.map((a) => (
              <button key={a.title} type="button" title={a.title} disabled={disabled} onClick={() => run(a)}>
                {a.label}
              </button>
            ))}
          </div>
          <textarea
            ref={ref}
            className="textarea md-editor-textarea"
            aria-label={ariaLabel}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            spellCheck
          />
          <div className="md-count">{value.length.toLocaleString()} characters · markdown supported</div>
        </>
      ) : (
        <div className="md-preview">
          {value.trim() ? (
            <Markdown markdown={value} />
          ) : (
            <p style={{ color: 'var(--color-text-faint)' }}>Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
